// * React
import React, { useState, useEffect } from "react";
// * Amplify
import { Amplify } from "aws-amplify";
import { CookieStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
// * Router-dom
import { Switch, Route } from "react-router-dom";
import { useLocation } from "react-router-dom";
// * Halstack
import { DxcApplicationLayout } from "@dxc-technology/halstack-react";
// * Pages or DesignSystem
import Home from "./Home/Home";
import GuidelinesApi from "./pages/apis/GuidelinesApis";
import SidenavApiCatalog from "./pages/apis/Sidenav";
import ApiCatalog from "./pages/apis/ApiCatalog";
import ToolsApis from "./pages/apis/Tools";
import GettingStarted from "./pages/apis/GettingStarted";
import Status from "./pages/apis/Status";
import Overview from "./DesignSystem/Overview/Overview";
import Help from "./DesignSystem/Help/Help";
import ToolsDesign from "./DesignSystem/Tools/Tools";
import ComponentGallery from "./DesignSystem/Overview/ComponentGallery";
// * Commons
import ScrollToTop from "./common/ScrollToTop";
import Header from "./common/Header";
import Dashboard from "./common/Dashboard";
import FooterContent from "./common/FooterContent";
import Login from "./common/Login";
// * Context or Services
import SessionContext from "./SessionContext.js";
import ApiCatalogContext from "./ApiCatalogContext";
import { initLogin, getUsername } from "./services/login.js";
// * Assets
import linkedinLogo from "./images/linkedin.svg";
import twitterLogo from "./images/twitter.svg";
import facebookLogo from "./images/facebook.svg";

// * Auth ( latest ) configure handled here!
Amplify.configure({
  Auth: {
    Cognito: {
      // * REQUIRED - Cognito User Pool ID
      userPoolId: window.assureConfig?.cognitoPoolId,
      // * REQUIRED - Cognito Client ID (26-char alphanumeric string)
      userPoolClientId: window.assureConfig?.cognitoAppClientId,
    },
  },
});

// * Amplify Token-saving mechanism
let cognitoAuthTokenStorage =
  window.assureConfig?.cognitoAuthTokenStorage || "";
// ? Check case sensitivity
cognitoAuthTokenStorage = cognitoAuthTokenStorage?.toString().toLowerCase();
if (cognitoAuthTokenStorage === "cookie") {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: window.assureConfig?.cookieStorage?.domain,
      path: window.assureConfig?.cookieStorage?.path || "/",
      secure: window.assureConfig?.cookieStorage?.secure || true,
      sameSite: window.assureConfig?.cookieStorage?.sameSite || "lax",
    })
  );
}

// * Custom Session
const useSession = () => {
  const [apiKey, setApiKey] = useState(null);
  const [authorization, setAuthorization] = useState(null);
  const [username, setUsername] = useState(null);
  const [isFetchingSession, setIsFetchingSession] = useState(false);

  useEffect(() => {
    setIsFetchingSession(true);
    initLogin((session) => {
      if (session) {
        // setApiKey(session);
        setApiKey(session.xApiKey);
        setAuthorization(session.Authorization);
        if (getUsername()) {
          // console.log('getUsername() :>> ', getUsername());
          setUsername(getUsername());
        }
      }
      setIsFetchingSession(false);
    });
  }, []);

  const resetSession = () => {
    setApiKey(null);
    setAuthorization(null);
    setUsername(null);
  };

  return {
    apiKey,
    authorization,
    username,
    isFetchingSession,
    setApiKey,
    setAuthorization,
    setUsername,
    resetSession,
  };
};

// * App
function App() {
  const session = useSession();
  const location = useLocation();
  const [searchString, setSearchString] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [dialogLogin, setDialogLogin] = useState({
    isLoginVisible: false,
    isRegisterVisible: false,
  });

  const hasSidenav = !location?.pathname?.includes("/apis/catalog");

  const handleLogin = () => {
    const locationPath = window.location.href;
    window.location.href = `${window.location.origin}${window.assureConfig.loginPath}?redirectPath=${locationPath}`;
  };

  const handleRegister = () => {
    const locationPath = window.location.href;
    window.location.href = `${window.location.origin}${window.assureConfig.loginPath}?redirectPath=${locationPath}`;
  };

  return (
    <SessionContext.Provider value={session}>
      <ApiCatalogContext.Provider
        value={{
          searchString,
          setSearchString,
          selectedRegion,
          setSelectedRegion,
        }}
      >
        <ScrollToTop />
        <DxcApplicationLayout
          visibilityToggleLabel="Menu"
          sidenav={
            !hasSidenav && (
              <>
                {location?.pathname?.includes("/apis/catalog") && (
                  <DxcApplicationLayout.SideNav
                    children={<SidenavApiCatalog />}
                  />
                )}
              </>
            )
          }
          header={
            <Header handleLogin={handleLogin} handleRegister={handleRegister} />
          }
          footer={
            <DxcApplicationLayout.Footer
              bottomLinks={[
                {
                  href: "https://www.dxc.technology",
                  text: "DXC Technology",
                },
                {
                  href: "https://www.dxc.technology/privacy",
                  text: "Privacy",
                },
                {
                  href: "https://www.dxc.technology/legal",
                  text: "Terms",
                },
              ]}
              socialLinks={[
                {
                  href: "https://www.linkedin.com/company/dxctechnology",
                  logo: linkedinLogo,
                },
                {
                  href: "https://twitter.com/dxctechnology",
                  logo: twitterLogo,
                },
                {
                  href: "https://www.facebook.com/DXCTechnology/",
                  logo: facebookLogo,
                },
              ]}
            >
              <FooterContent />
            </DxcApplicationLayout.Footer>
          }
        >
          <DxcApplicationLayout.Main>
            {dialogLogin.isLoginVisible && <Login handleLogin={handleLogin} />}
            {dialogLogin.isRegisterVisible && (
              <Login handleRegister={handleRegister} />
            )}
            <Switch>
              <Route exact path="/">
                <Home />
              </Route>
              <Route exact path="/dashboard">
                <Dashboard />
              </Route>
              <Route exact path="/getting-started">
                <GettingStarted />
              </Route>
              <Route exact path="/design">
                <Overview />
              </Route>
              <Route exact path="/design/component-gallery">
                <ComponentGallery />
              </Route>
              <Route path="/design/help">
                <Help />
              </Route>
              <Route path="/design/tools">
                <ToolsDesign />
              </Route>
              <Route exact path="/apis">
                <GuidelinesApi />
              </Route>
              <Route path="/apis/catalog">
                <ApiCatalog />
              </Route>
              <Route path="/apis/tools">
                <ToolsApis />
              </Route>
              <Route path="/apis/status">
                <Status />
              </Route>
              <Route component={() => <h2>Page not found</h2>} />
            </Switch>
          </DxcApplicationLayout.Main>
        </DxcApplicationLayout>
      </ApiCatalogContext.Provider>
    </SessionContext.Provider>
  );
}

export default App;
